/**
 * User-management widgets (#1805 slice A3a).
 *
 * All four columns of the users list carried `fields[].render`, which has no v2
 * carrier, so each becomes a named widget here. Every one of them reads the
 * RECORD rather than the cell value, because that is what the legacy renderers
 * did: the email cell needs `id` as well as `email` to build its row link, the
 * role badge picks both its label and its colour off `role`, and the status cell
 * branches on `status`. A widget written against `value` would still look right
 * on three of the four and would silently break the link target on the first.
 *
 * Every widget declares `csvText: rawCsv`, and that is load-bearing rather than
 * ceremonial. The legacy `render` carrier exports `stringifyRaw(value)` — the
 * bespoke-renderer branch of `formatFieldTextForExport` — which is `'' → ''`.
 * Omit `csvText` and the export falls through to the field's `text` type
 * instead, whose projection is `'' → -`, and nothing on screen changes to
 * betray it. `csv-export-baseline.json` freezes all four columns at the raw
 * text; `__tests__/usersResource.v2-parity.test.tsx` restates it locally.
 *
 * No `@services/*` import, deliberately: `src/features/widgets.ts` is evaluated
 * by `src/test-setup.ts` for EVERY suite, ahead of any `vi.mock`, so a service
 * reached from a widget module is instantiated against the real `@lib/api`
 * before a test can replace it. `@/permissions` is a zero-import constant
 * catalog and `@/i18n` is the singleton the legacy renderers already used.
 */
import { Link } from 'react-router-dom';
import i18n from '@/i18n';
import { ROLE_LABELS } from '@/permissions';
import { stringifyRaw } from '@resource-kit/ui/react';
import { registerWidget } from '@resource-kit/controllers';
import type { WidgetCsvContext, WidgetProps } from '@resource-kit/model';

declare module '@resource-kit/core' {
  interface WidgetRegistry {
    userEmailLink: { types: 'text'; modes: 'list' };
    userName: { types: 'text'; modes: 'list' };
    userRoleBadge: { types: 'text'; modes: 'list' };
    userStatus: { types: 'text'; modes: 'list' };
  }
}

const EM_DASH = '—';

/** The legacy `render` carrier's CSV projection, kept exactly. */
const rawCsv = ({ value }: WidgetCsvContext): string => stringifyRaw(value);

/**
 * One member of the user row, as text.
 *
 * `User` types all four of these as strings, but these projections are reached
 * with an erased record (the CSV path and the architecture fixtures both pass
 * `never`), so a missing or null member degrades to the empty string exactly as
 * React renders `null` in a text slot — rather than throwing inside a cell.
 */
function memberOf(record: unknown, key: string): string {
  if (typeof record !== 'object' || record === null) return '';
  const value: unknown = (record as Record<string, unknown>)[key];
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
}

/**
 * The email cell is the row's link to `/users/:id`.
 *
 * Note the asymmetry, preserved verbatim from the bespoke page: the href is
 * `encodeURIComponent`-escaped and the test id is NOT. The test id is addressed
 * by `Users.table.test.tsx` and the e2e specs, so escaping it would look like a
 * tidy-up and break them.
 */
function UserEmailLink({ record }: WidgetProps) {
  const id = memberOf(record, 'id');
  return (
    <Link
      to={`/users/${encodeURIComponent(id)}`}
      data-testid={`user-row-link-${id}`}
      className="text-accent hover:text-accent-hover hover:underline underline-offset-2"
    >
      {memberOf(record, 'email')}
    </Link>
  );
}

/** `||`, not `??`: an EMPTY name must reach the dash, not print as blank. */
function UserName({ record }: WidgetProps) {
  return <span className="text-muted-foreground">{memberOf(record, 'name') || EM_DASH}</span>;
}

/**
 * Role badge, ported verbatim from the bespoke page.
 *
 * Both halves fall back on an out-of-set role: the label degrades to the raw
 * value through `i18n.t`'s `defaultValue`, and the colour degrades to `viewer`'s
 * neutral chip — so an unrecognised role stays legible instead of rendering an
 * unstyled or blank cell.
 */
const roleBadgeStyles: Record<string, string> = {
  admin: 'bg-primary/15 text-primary border-primary/20',
  operator: 'bg-accent/15 text-accent border-accent/20',
  viewer: 'bg-secondary text-foreground border-border',
};

function UserRoleBadge({ record }: WidgetProps) {
  const role = memberOf(record, 'role');
  const raw = ROLE_LABELS[role] || role;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${roleBadgeStyles[role] || roleBadgeStyles.viewer}`}
    >
      {i18n.t(raw, { defaultValue: raw })}
    </span>
  );
}

/**
 * Only `active` is translated; every other status prints its raw wire value.
 * That is the pre-existing behaviour, not an oversight to fix here — changing
 * it is a copy decision, and this slice is behaviour-preserving.
 */
function UserStatus({ record }: WidgetProps) {
  const status = memberOf(record, 'status');
  return (
    <span
      className={`text-xs ${status === 'active' ? 'text-foreground' : 'text-muted-foreground'}`}
    >
      {status === 'active' ? i18n.t('users.statusActive') : status}
    </span>
  );
}

/** Called by the feature barrel, which the composition root calls once. */
export function registerUserWidgets(): void {
  // These rows declare no `input`: resolution refills it from the field type
  // default (ADR-0030).
  const shared = {
    csvText: rawCsv,
    supportedTypes: ['text'],
    modes: ['list'],
  } as const;

  registerWidget('userEmailLink', { display: UserEmailLink, ...shared });
  registerWidget('userName', { display: UserName, ...shared });
  registerWidget('userRoleBadge', { display: UserRoleBadge, ...shared });
  registerWidget('userStatus', { display: UserStatus, ...shared });
}
